import { useEffect } from 'react';
import { useMediaQuery, NARROW_QUERY } from '@/shared/hooks/useMediaQuery';
import type { WidgetInstance } from './types';
import type { Box } from './place';
import { getWidgetDef, nodeForWidget } from './registry';
import { ROW_H, GAP, GRID_COLS } from './resize';
import { bottomRow } from './place';
import { resolveContainerBoxes } from './placeContainer';
import { useIsHidden } from '@/shared/permissions/hooks';
import { WidgetCard } from './WidgetCard';
import { homeEntranceActive, markHomeEntrancePlayed } from './homeEntrance';

// The login "wow": the first Home render of a session staggers the widgets
// in (rise + fade, ordered by grid position). Subsequent visits are instant.

function WidgetFrame({ w, box, animate, order, flow }: { w: WidgetInstance; box: Box; animate: boolean; order: number; flow?: 'tile' | 'block' }) {
  const hidden = useIsHidden(nodeForWidget(w));
  const def = getWidgetDef(w.type);
  if (!def || hidden) return null;
  const Render = def.Render;
  // Show the widget title in view mode too — EXCEPT compact tiles (KPI), which
  // are tiny and self-label with their metric name, so a header would crowd them.
  return (
    <WidgetCard def={def} title={def.title?.(w.config)} headerAction={def.headerAction?.(w.config)} bare={!!def.compact} compact={!!def.compact}
      className={animate ? 'home-enter' : undefined}
      style={{
        ...(flow
          ? { height: flow === 'tile' ? ROW_H : box.h * ROW_H + (box.h - 1) * GAP }
          : { gridColumn: `${box.x + 1} / span ${box.w}`, gridRow: `${box.y + 1} / span ${box.h}` }),
        ...(animate ? { animationDelay: `${Math.min(order * 55, 660)}ms` } : {}),
      }}>
      <Render config={w.config} w={flow === 'tile' ? 2 : flow === 'block' ? GRID_COLS : box.w} h={box.h} widgetId={w.id} />
    </WidgetCard>
  );
}

export function WidgetGridView({ widgets }: { widgets: WidgetInstance[] }) {
  const animate = homeEntranceActive();
  // One play per login: consume the latch once this render's animation is
  // committed, so navigating back to Home doesn't replay the entrance.
  useEffect(() => { if (animate) markHomeEntrancePlayed(); }, [animate]);
  const narrow = useMediaQuery(NARROW_QUERY);
  if (!widgets.length) return null;
  const boxes = resolveContainerBoxes(widgets);
  const byId = new Map(boxes.map((b) => [b.id, b]));
  const rows = Math.max(bottomRow(boxes), 1);
  // Stagger order follows reading order: top row first, left to right.
  const orderOf = (b: Box) => b.y * 100 + b.x;
  const sorted = [...widgets].sort((a, b2) => orderOf(byId.get(a.id) as Box) - orderOf(byId.get(b2.id) as Box));
  const orderIdx = new Map(sorted.map((w, i) => [w.id, i]));
  if (narrow) {
    // Phone (layout-01): the saved layout is untouched; the view reflows into
    // one column in reading order, compact KPI tiles two to a row.
    const runs: Array<{ compact: boolean; items: WidgetInstance[] }> = [];
    for (const w of sorted) {
      const compact = !!getWidgetDef(w.type)?.compact;
      const last = runs[runs.length - 1];
      if (compact && last?.compact) last.items.push(w);
      else runs.push({ compact, items: [w] });
    }
    return (
      <div data-home-flow="phone" className="flex flex-col" style={{ gap: GAP }}>
        {runs.map((run) => run.compact ? (
          <div key={run.items[0].id} data-home-kpis="" className="grid grid-cols-2" style={{ gap: GAP }}>
            {run.items.map((w) => <WidgetFrame key={w.id} w={w} box={byId.get(w.id) as Box} animate={animate} order={orderIdx.get(w.id) ?? 0} flow="tile" />)}
          </div>
        ) : run.items.map((w) => <WidgetFrame key={w.id} w={w} box={byId.get(w.id) as Box} animate={animate} order={orderIdx.get(w.id) ?? 0} flow="block" />))}
      </div>
    );
  }
  return (
    <div className="grid" style={{
      gridTemplateColumns: `repeat(${GRID_COLS},minmax(0,1fr))`,
      gridTemplateRows: `repeat(${rows}, ${ROW_H}px)`,
      gridAutoRows: `${ROW_H}px`,
      gap: GAP,
    }}>
      {widgets.map((w) => <WidgetFrame key={w.id} w={w} box={byId.get(w.id) as Box} animate={animate} order={orderIdx.get(w.id) ?? 0} />)}
    </div>
  );
}
